import { useEffect, useState } from 'react';
import { ApiOutlined, CloudServerOutlined, CloudSyncOutlined, DatabaseOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, InputNumber, Switch, Tag } from 'antd';
import { api } from '../../api';
import { DataTable, ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { BackToSettings } from './BackToSettings';
import { formatBytes, formatDateTime, timeAgo, useLoad } from '../../lib';
export function CloudStorageSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [dirty, setDirty] = useState(false);
    const { data, error, loading, reload } = useLoad(async () => {
        const [storage, synced] = await Promise.all([
            api('/v1/admin/storage'),
            api('/v1/admin/sync-files')
        ]);
        return { settings: storage.settings, files: synced.files };
    });
    const settings = data?.settings ?? null;
    const files = data?.files ?? [];
    const stored = files.reduce((sum, file) => sum + file.sizeBytes, 0);
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ ...settings, applicationKey: '' });
        setDirty(false);
    }, [settings, form]);
    async function save(values) {
        setSaving(true);
        try {
            const body = await api('/v1/admin/storage', { method: 'PUT', body: JSON.stringify(values) });
            message.success(body.settings.enabled ? 'Saved. Cloud sync is on.' : 'Saved. Cloud sync is off.');
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function runTest() {
        try {
            await form.validateFields(['endpoint', 'bucket', 'keyId', 'applicationKey']);
        }
        catch {
            return;
        }
        setTesting(true);
        setTest(null);
        try {
            await api('/v1/admin/storage/test', { method: 'POST', body: JSON.stringify(form.getFieldsValue()) });
            setTest({ ok: true, text: 'Connected. A test file was written to the bucket and removed.' });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The connection test failed' });
        }
        finally {
            setTesting(false);
        }
    }
    return (<>
      <PageHeader title="Cloud storage" description="Where companies synced from the desktop app are kept. The app gets short-lived links from the API and never sees these keys." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      {settings?.keyUnreadable && (<Alert type="warning" showIcon title="The saved application key cannot be read on this server" description="The server's settings key was replaced or lost, so sync is off until the key is entered again. Your other settings are kept." style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="Sync" tone={settings?.enabled ? 'green' : 'slate'} icon={<CloudSyncOutlined />} loading={loading && !data} value={settings?.enabled ? 'On' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Synced companies" tone="sky" icon={<DatabaseOutlined />} loading={loading && !data} value={files.length} foot={`${new Set(files.map((file) => file.accountId)).size} accounts`}/>
        <StatCard label="Stored" tone="indigo" icon={<CloudServerOutlined />} loading={loading && !data} value={formatBytes(stored)} foot="Latest copy of each company"/>
      </div>

      {loading && !data ? (<Panel flush title="Backblaze B2" description="Uses B2's S3-compatible API. No AWS account is needed.">
          <FormSkeleton sections={4}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={save} onValuesChange={() => setDirty(true)} initialValues={{ enabled: false, prefix: 'kivonex/', maxUploadMb: 500 }}>
        <Panel flush title="Backblaze B2" description="Uses B2's S3-compatible API. No AWS account is needed." extra={settings?.enabled ? <Tag color="success" variant="filled"><span className="status-dot"/>Connected for sync</Tag> : <Tag variant="filled">Sync off</Tag>}>
          <div className="form-section">
            <div>
              <h4>Sync</h4>
              <p>When off, the desktop app cannot upload or download. Files already in the bucket are kept.</p>
            </div>
            <div className="switch-row" style={{ marginBottom: 0 }}>
              <div>
                <strong>Allow cloud sync</strong>
                <span>Customers signed in to Kivonex Cloud can sync their companies.</span>
              </div>
              <Form.Item name="enabled" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Bucket</h4>
              <p>Shown on the bucket's page in B2. Make the bucket private and turn on its server-side encryption.</p>
            </div>
            <div>
              <Form.Item label="S3 endpoint" name="endpoint" rules={[{ required: true, message: 'Enter the endpoint.' }]}>
                <Input placeholder="s3.us-west-004.backblazeb2.com" prefix={<ApiOutlined style={{ color: 'var(--muted)' }}/>}/>
              </Form.Item>
              <div className="form-row">
                <Form.Item label="Bucket name" name="bucket" rules={[{ required: true, message: 'Enter the bucket name.' }]}>
                  <Input placeholder="kivonex-sync"/>
                </Form.Item>
                <Form.Item label="Region" name="region" extra="Read from the endpoint when blank.">
                  <Input placeholder="us-west-004"/>
                </Form.Item>
              </div>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Application key</h4>
              <p>Create a key limited to this bucket with read and write access. It is stored encrypted and never shown again.</p>
            </div>
            <div>
              <Form.Item label="Key ID" name="keyId" rules={[{ required: true, message: 'Enter the key ID.' }]}>
                <Input placeholder="0051a2b3c4d5e6f0000000001" autoComplete="off" className="mono"/>
              </Form.Item>
              <Form.Item label="Application key" name="applicationKey" rules={[{ required: !settings?.applicationKeySet, message: 'Enter the application key.' }]} extra={settings?.applicationKeySet ? (<span>
                      <SafetyCertificateOutlined style={{ color: 'var(--brand)' }}/> A key ending <span className="mono">{settings.applicationKeyHint}</span> is saved. Leave blank to keep it.
                    </span>) : ('B2 shows it once, when the key is created.')}>
                <Input.Password autoComplete="new-password" placeholder={settings?.applicationKeySet ? '••••••••••••' : ''}/>
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <div>
              <h4>Files</h4>
              <p>Each company is kept under the folder as account / company. The latest copy and one before it are kept.</p>
            </div>
            <div className="form-row">
              <Form.Item label="Folder in bucket" name="prefix">
                <Input placeholder="kivonex/" className="mono"/>
              </Form.Item>
              <Form.Item label="Largest company file" name="maxUploadMb" rules={[{ required: true }]}>
                <InputNumber placeholder="500" min={1} max={5000} suffix="MB" style={{ width: '100%' }}/>
              </Form.Item>
            </div>
          </div>

          {test && (<div style={{ padding: '0 20px 20px' }}>
              <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
            </div>)}
          <div className="form-actions">
            <Button onClick={() => void runTest()} loading={testing}>
              Test connection
            </Button>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>
              Save changes
            </Button>
          </div>
        </Panel>
      </Form>)}

      <Panel flush title="Synced companies" description="The cloud copy of every company uploaded from the desktop app." className="panel-gap">
        <DataTable rowKey="id" loading={loading} skeletonRows={3} dataSource={files} pagination={files.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 820 }} locale={{ emptyText: 'Companies appear here after a desktop app uploads one.' }} columns={[
            {
                title: 'Company',
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  <strong style={{ fontWeight: 550 }}>{row.companyName}</strong>
                  <span className="cell-sub">{row.accountEmail}</span>
                </span>)
            },
            { title: 'Version', dataIndex: 'version', width: 100, align: 'right', render: (value) => <span className="num">v{value}</span> },
            { title: 'Size', dataIndex: 'sizeBytes', width: 110, align: 'right', render: (value) => <span className="num">{formatBytes(value)}</span> },
            { title: 'Uploaded from', dataIndex: 'deviceName', width: 200, render: (value) => value || '—' },
            {
                title: 'Last upload',
                dataIndex: 'updatedAt',
                width: 190,
                skeleton: 'lines',
                render: (value) => (<span>
                  {timeAgo(value)}
                  <span className="cell-sub">{formatDateTime(value)}</span>
                </span>)
            }
        ]}/>
      </Panel>
    </>);
}
